import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { UPDATE_PROFILE_ANSWER_MUTATION } from "@/api/mutations/updateProfileAnswer";
import {
  UpdateProfileAnswerMutation,
  UpdateProfileAnswerMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";

export interface UseUpdateProfileAnswerParams {
  onCompleted?: (data: UpdateProfileAnswerMutation) => void;
  onError?: (error: unknown) => void;
}

export interface UpdateProfileAnswerResult {
  success: boolean;
  errors: {
    message: string;
    title: string;
  }[];
}

export interface UseUpdateProfileAnswerReturn {
  loading: boolean;
  error: unknown;
}

export interface UpdateProfileAnswerInput {
  userId: string;
  profileAnswerId: string;
  answer: string;
}

export const useUpdateProfileAnswer = ({
  onCompleted,
  onError,
}: UseUpdateProfileAnswerParams = {}): [
  (input: UpdateProfileAnswerInput) => Promise<UpdateProfileAnswerResult>,
  UseUpdateProfileAnswerReturn,
] => {
  const [updateProfileAnswerMutation, { loading, error }] = useMutation<
    UpdateProfileAnswerMutation,
    UpdateProfileAnswerMutationVariables
  >(UPDATE_PROFILE_ANSWER_MUTATION, {
    onCompleted,
    onError,
    refetchQueries: ["ProfileQuestions", "ProfileAnswers"],
    awaitRefetchQueries: true,
  });

  const updateProfileAnswer = useCallback(
    async (
      input: UpdateProfileAnswerInput,
    ): Promise<UpdateProfileAnswerResult> => {
      try {
        const result = await updateProfileAnswerMutation({
          variables: {
            userId: input.userId,
            profileAnswerId: input.profileAnswerId,
            answer: input.answer,
          },
        });

        const errorHandler = MutationErrorHandler.fromResult(
          result,
          result.errors,
        );
        return errorHandler.toResult();
      } catch (error) {
        return {
          success: false,
          errors: [
            {
              message: error instanceof Error ? error.message : "Unknown error",
              title: "Mutation Error",
            },
          ],
        };
      }
    },
    [updateProfileAnswerMutation],
  );

  return [updateProfileAnswer, { loading, error }];
};
